import { Key, useEffect, useReducer, useState } from 'react';

import {
  getNumberOfChildrenChecked,
  hasDescendantsChecked,
  matchesSearch,
  setDescendantChecked,
  setIsCheckedAction,
  toggleIsExpanded,
  treeMatchesSearch,
  updateCheckedKeysAndGetCheckedState,
} from './tree-utils';
import {
  TreeDataNode,
  TreeInternalNodeProps,
  TreeLeafProps,
  TreeNodeAction,
  TreeNodeProps,
  TreeNodeState,
  TreeRootProps,
} from './tree.types';
import { ChevronDownIcon, Checkbox } from '../..';
import { highlight } from '../filter-combo-box/filter-combo-box-menu-item';

const nodeChildrenClasses = 'ml-10';

export const treeNodeReducer = (state: TreeNodeState, action: TreeNodeAction) => {
  switch (action.type) {
    case 'setIsChecked': {
      return setIsCheckedAction(state, action.isChecked, action.isHalfChecked);
    }
    case 'toggleIsExpanded': {
      return toggleIsExpanded(state, state.isExpanded);
    }
    case 'setIsExpanded': {
      return { ...state, isExpanded: action.isExpanded };
    }
  }
};

const treeNodeStateInit = (state: TreeNodeState) => {
  if (state.checkedKeys.current.has(state.id)) {
    for (const child of state.children) {
      state.checkedKeys.current.add(child.id);
    }
  }

  const numberChildrenChecked = getNumberOfChildrenChecked(state.children, state.checkedKeys);

  return {
    ...state,
    isChecked: state.isChecked,
    isHalfChecked: numberChildrenChecked > 0 && numberChildrenChecked !== state.children.length,
    isExpanded: state.isExpanded,
  };
};

// eslint-disable-next-line max-lines-per-function
const TreeInternalNode = (props: TreeInternalNodeProps) => {
  const [state, dispatch] = useReducer(
    treeNodeReducer,
    {
      isChecked: props.checked,
      isExpanded: props.expanded,
      children: props.childrenData,
      checkedKeys: props.checkedKeys,
      expandedKeys: props.expandedKeys,
      onCheck: props.onCheck,
      onCheckCallback: props.onCheckCallback,
      onExpandCallback: props.onExpandCallback,
      id: props.id,
    } as TreeNodeState,
    treeNodeStateInit
  );

  useEffect(() => {
    if (props.checked !== undefined) {
      const shouldBeHalfChecked = !props.checked && hasDescendantsChecked(props.checkedKeys, props.childrenData);
      dispatch({ type: 'setIsChecked', isChecked: props.checked, isHalfChecked: shouldBeHalfChecked });
    }
  }, [props.checked, props.checkedKeys, props.childrenData, props.id]);

  useEffect(() => {
    if (props.expanded !== undefined) dispatch({ type: 'setIsExpanded', isExpanded: props.expanded });
  }, [props.expanded]);

  // treeMatchesSearch will return true if the current node is a match or if any children are matches
  // Some descendents will be hidden, but the parent node should still render
  if (
    props.searchTerm &&
    !props.parentMatchesSearch &&
    !treeMatchesSearch(
      { value: props.value, title: props.title, id: props.id, children: props.childrenData },
      props.searchTerm
    )
  ) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  return (
    <>
      <div className="flex px-2.5 py-1">
        <ChevronDownIcon
          onClick={() => dispatch({ type: 'toggleIsExpanded' })}
          className={`size-6 cursor-pointer transition-all ${state.isExpanded ? 'rotate-180	' : 'rotate-0'}`}
        />
        <Checkbox
          value={props.value}
          label={highlight(props.title, props.searchTerm)}
          isChecked={state.isChecked}
          isPartiallyChecked={state.isHalfChecked}
          onChange={(e) => {
            const checked = e.target.checked;
            setDescendantChecked(checked, state);
            props.onCheck(checked, props.id);
            dispatch({ type: 'setIsChecked', isChecked: checked, isHalfChecked: false });
          }}
        />
      </div>
      {state.isExpanded &&
        props.childrenData.map((node: TreeDataNode) => {
          return (
            <div key={node.id} className={nodeChildrenClasses}>
              <TreeNode
                id={node.id}
                value={node.value}
                title={node.title}
                childrenData={node.children}
                level={props.level + 1}
                expanded={state.expandedKeys.current.has(node.id)}
                checked={state.checkedKeys.current.has(node.id)}
                checkedKeys={props.checkedKeys}
                expandedKeys={props.expandedKeys}
                onExpandCallback={props.onExpandCallback}
                searchTerm={props.searchTerm}
                onCheckCallback={props.onCheckCallback}
                // Need to check if current node is a match. If so, all children should render
                // Also, if ancestor is an exact match, descendents should render
                parentMatchesSearch={props.parentMatchesSearch || matchesSearch(props, props.searchTerm)}
                onCheck={(checked: boolean) => {
                  const { shouldBeChecked, shouldBeHalfChecked } = updateCheckedKeysAndGetCheckedState(
                    props.id,
                    checked,
                    node.id,
                    props.childrenData,
                    props.checkedKeys
                  );

                  if (!state.isChecked && shouldBeChecked) {
                    props.onCheck(true, props.id);
                  } else if ((state.isChecked && !shouldBeChecked) || state.isHalfChecked !== shouldBeHalfChecked) {
                    props.onCheck(false, props.id);
                  } else {
                    // Changes won't cascade any further, so call callback here
                    props.onCheckCallback?.(props.checkedKeys.current);
                  }

                  dispatch({ type: 'setIsChecked', isChecked: shouldBeChecked, isHalfChecked: shouldBeHalfChecked });
                }}
              />
            </div>
          );
        })}
    </>
  );
};

const TreeLeafNode = (props: TreeLeafProps) => {
  const { id, checked: defaultIsChecked, value, title, onCheck, searchTerm, parentMatchesSearch } = props;
  const [isChecked, setIsChecked] = useState(defaultIsChecked);

  useEffect(() => {
    setIsChecked(defaultIsChecked);
  }, [defaultIsChecked]);

  // eslint-disable-next-line unicorn/no-null
  if (searchTerm && !parentMatchesSearch && !treeMatchesSearch({ value, title, id }, searchTerm)) return null;

  return (
    <div className="ml-8.5 py-1">
      <Checkbox
        value={value}
        isChecked={isChecked}
        label={highlight(title, searchTerm)}
        onChange={(e) => {
          setIsChecked(e.target.checked);
          onCheck(e.target.checked, id);
        }}
      />
    </div>
  );
};

export const TreeNode = (props: TreeNodeProps) => {
  return props.childrenData ? (
    <TreeInternalNode {...(props as TreeInternalNodeProps)} />
  ) : (
    <TreeLeafNode checked={props.checked || props.checkedKeys.current.has(props.id)} {...props} />
  );
};

export const TreeRoot = (props: TreeRootProps) => {
  return (
    <TreeNode
      id={props.id}
      value={props.value}
      title={props.title}
      checked={props.isChecked}
      expanded={props.isExpanded}
      childrenData={props.children}
      checkedKeys={props.checkedKeys}
      expandedKeys={props.expandedKeys}
      onCheckCallback={props.onCheck}
      onExpandCallback={props.onExpand}
      searchTerm={props.searchTerm}
      parentMatchesSearch={matchesSearch(props, props.searchTerm)}
      level={0}
      onCheck={(checked: boolean, key: Key) => {
        if (checked) {
          props.checkedKeys.current.add(key);
        }
        props.onCheck?.(props.checkedKeys.current);
      }}
    />
  );
};
